import {
  BannerTypeKeys,
  GenerateLinkParams,
  getMediaTemplateFromObject,
  getUnknownTemplate,
  InvoiceMessage,
  Media,
  MessageAction,
  MessageType,
  OrderMessage,
  PaymentMessage,
  TemplateKeys,
  TemplateParams,
} from '@zonofi/common';
import { OrderSubTag, PaymentSubTag, SupportSubTag } from '../types';
import { i18nService } from '@zonofi/i18n';
import {
  getCampaignParams,
  getInvoiceTemplateParams,
  getListTemplateParams,
  getOrderTemplateParams,
  getPaymentTemplateParams,
} from './generate-tempate';
import { isEmpty } from 'lodash';

const extractSubtype = (url: any): string => {
  if (typeof url === 'object') url = url?.[0]?.url;
  if (typeof url !== 'string' || !url) return 'official';

  try {
    const cleanedUrl = url.replace(/^[a-zA-Z]+:\/\//, '').split(/[?#]/)[0];
    const pathParts = cleanedUrl.split('/').filter(Boolean);

    const subtype = pathParts.find((part) =>
      ['official', 'provisional'].includes(part.toLowerCase())
    );
    return subtype || 'official';
  } catch {
    return 'official';
  }
};

export const generateMediaParams = (
  type: MessageType,
  srcdata: any,
  mimeType: 'image' | 'document',
  media: Media,
  messageId: string
): GenerateLinkParams => {
  const data =
    Array.isArray(srcdata) && srcdata.length > 0 ? srcdata[0] : srcdata;
  switch (type) {
    case MessageType.INVOICE:
      return {
        documentType: 'invoice',
        entityId: data?.invoiceId || data?.id,
        mimeType: mimeType === 'image' ? 'image/*' : 'application/pdf',
        subType: data?.docs ? extractSubtype(data?.docs) : 'official',
      };
    case MessageType.DISPATCH: {
      return {
        documentType: 'dispatch',
        entityId: data?.dispatchId || data?.id,
        mimeType: mimeType === 'image' ? 'image/*' : 'application/pdf',
        // subType: 'courier_receipt',
      };
    }
    default:
      return {
        cntType: media?.cntType,
        name: media?.name ?? media?._id,
        messageId: messageId,
      };
  }
};

export const getMessageActionsData = (
  actions: MessageAction,
  type: string
): Record<string, string> => {
  const userActions = actions?.[type] || {};

  const data = Object.entries(userActions).map(([key, value]) => {
    const timetoken = value?.[0]?.actionTimetoken as string;
    return [key, timetoken];
  });
  const sortedData = data.sort((a, b) => Number(b[1]) - Number(a[1]));
  return Object.fromEntries(sortedData);
};

export const getCampaignStatus = (
  type: TemplateKeys,
  actions: Record<string, string>
): { type: BannerTypeKeys; timetoken: string; text: string } | null => {
  if (type === 'campaign') {
    if ('Approved' in actions) {
      return {
        type: 'order_approved',
        timetoken: actions['Approved'],
        text: i18nService.t('chats:VIEW_ORDER'),
      };
    }
    if ('SubmittedByCustomer' in actions) {
      return {
        type: 'order_submitted',
        timetoken: actions['SubmittedByCustomer'],
        text: i18nService.t('chats:ACCEPT_ORDER'),
      };
    }
    if ('AddingItems' in actions) {
      return {
        type: 'order_draft',
        timetoken: actions['AddingItems'],
        text: i18nService.t('chats:CHECKOUT_ORDER'),
      };
    }
    if ('EX_MESSAGE_ENGAGED' in actions || 'MESSAGE_ENGAGED' in actions) {
      return {
        type: 'engaged',
        timetoken: actions['EX_MESSAGE_ENGAGED'] || actions['MESSAGE_ENGAGED'],
        text: i18nService.t('chats:CONVERT_CREATE_ORDER'),
      };
    }
    if ('EX_MESSAGE_SEEN' in actions || 'MESSAGE_SEEN' in actions) {
      return {
        type: 'impression',
        timetoken: actions['EX_MESSAGE_SEEN'] || actions['MESSAGE_SEEN'],
        text: i18nService.t('chats:ENGAGE_CREATE_ORDER'),
      };
    }
  }
  return null;
};

export function isOrderSubTag(item: string): item is OrderSubTag {
  return Object.values(OrderSubTag).includes(item as OrderSubTag);
}

export function isPaymentSubTag(item: string): item is PaymentSubTag {
  return Object.values(PaymentSubTag).includes(item as PaymentSubTag);
}

export function isSupportSubTag(item: string): item is SupportSubTag {
  return Object.values(SupportSubTag).includes(item as SupportSubTag);
}

export const getMessageTagColor = (tag: string): string => {
  const isOrderTag = isOrderSubTag(tag);
  const isPaymentTag = isPaymentSubTag(tag);
  if (tag === '' || tag === null) return 'transparent';
  if (isOrderTag) return '#DD360C';
  if (isPaymentTag) return '#2ABB7F';
  return '#8C8C8C';
};

export const createTemplateParamsMap = (
  data: any,
  sellerName: string,
  channelName: string,
  media: any[]
): Record<TemplateKeys, TemplateParams> => {
  const orderTemplates = getOrderTemplateParams(
    data as OrderMessage,
    sellerName
  );
  const invoiceTemplate = getInvoiceTemplateParams(
    data as InvoiceMessage,
    sellerName
  );
  const paymentTemplate = getPaymentTemplateParams(
    data as PaymentMessage,
    sellerName,
    channelName || '',
    media?.[0]?.cUrl
  );
  const listTemplate = getListTemplateParams(data);
  const campaignTemplate = getCampaignParams(data);

  return {
    ...orderTemplates,
    ...invoiceTemplate,
    ...paymentTemplate,
    ...listTemplate,
    ...campaignTemplate,
    media: [],
    unknown: [],
  } as Record<TemplateKeys, TemplateParams>;
};

export const handleDataBasedTemplates = (
  data: any,
  templateName: TemplateKeys | undefined,
  sellerName: string,
  channelName: string,
  media: any[],
  text: string,
  fileUrl: string | undefined
) => {
  if (templateName === 'media') {
    return { template: getMediaTemplateFromObject(media, text, fileUrl) };
  }

  if (templateName === 'unknown' && !isEmpty(text)) {
    return { template: getUnknownTemplate(text) };
  }

  return null;
};
